/**
 * 折线自相交检测工具。
 *
 * 设计说明：
 * 1. 纯函数实现：无 class、无副作用、不依赖 three.js，输入输出均为纯数据（{x, y, z} 点列），
 *    主线程与 Worker 均可直接使用，与 tool/ 下其它样条几何工具保持同一形态；
 * 2. 加速结构：使用"空间哈希网格"（Spatial Hash Grid）替代朴素两两检测。
 *    每条边按其 AABB（轴对齐包围盒）覆盖的所有网格单元"全覆盖插入"，
 *    查询时只对同格边对做精确线段求交。正确性保证：若两条边的 AABB 相交，
 *    则它们必然共享至少一个网格单元，因此候选筛选不漏报，判定结果与 O(n²) 两两检测完全等价；
 * 3. 边界豁免：顺序相邻的两条边共享端点，属于连续折线的合法连接，不判自交；
 *    闭合折线首尾点重合时，首边与末边共享接缝端点，同样豁免；
 * 4. 线段级近似说明：检测对象是输入采样点列构成的折线，无法发现"相邻采样点之间"的
 *    曲线交叉（采样密度不足时可能漏检），这是所有折线检测方法的共性，
 *    调用方应保证输入采样密度足以表达目标曲线形态；
 * 5. 本文件只承载检测能力本身，不负责业务接入（调用方为草图样条对象层收口点）。
 */

/**
 * 折线自相交检测输入点结构。
 * 说明：检测在 x/y 平面内进行（草图几何位于同一平面），z 仅随交点参数插值透传，不参与判定。
 */
export interface PolylinePointLike {
    /** X 坐标。 */
    x: number;
    /** Y 坐标。 */
    y: number;
    /** Z 坐标。 */
    z: number;
}

/**
 * 折线自相交检测选项。
 */
export interface PolylineSelfIntersectionOptions {
    /**
     * 是否按闭合折线检测。
     * 说明：
     * 1. 为 true 时，首边与末边在"首尾点重合"的前提下共享接缝端点，视为合法连接而豁免；
     * 2. 若声明闭合但首尾点实际不重合，接缝边对按普通边对参与检测（保守语义）。
     */
    closed?: boolean;
    /**
     * 距离容差（世界单位）。
     * 说明：用于端点重合、共线、平行判定；未提供时按输入点列包围盒对角线取相对容差（1e-9 比例），
     * 以适配不同坐标尺度（米/毫米），下限 1e-12 防止零包围盒退化。
     */
    epsilon?: number;
}

/**
 * 一条自相交边对及其代表交点。
 */
export interface PolylineSelfIntersectionEdgePair {
    /** 第一条边的索引（边 i 表示 points[i] → points[i+1]）。 */
    edgeIndexA: number;
    /** 第二条边的索引（恒大于 edgeIndexA）。 */
    edgeIndexB: number;
    /** 代表交点：一般相交为唯一交点，共线重叠为重叠区间中点，端点接触为接触点。 */
    point: PolylinePointLike;
}

/**
 * 折线自相交检测结果。
 */
export interface PolylineSelfIntersectionResult {
    /** 是否存在自相交（含自触、共线重叠）。 */
    isSelfIntersecting: boolean;
    /** 全部代表交点列表（与 edgePairs 一一对应）。 */
    intersectionPoints: PolylinePointLike[];
    /** 全部相交边对列表；为空表示无自相交。 */
    edgePairs: PolylineSelfIntersectionEdgePair[];
}

/**
 * 内部边数据：边 i 的端点、AABB 与长度信息，供网格插入与精确求交复用，避免重复计算。
 */
interface PolylineEdgeData {
    /** 起点（points[i]）。 */
    a: PolylinePointLike;
    /** 终点（points[i + 1]）。 */
    b: PolylinePointLike;
    /** AABB 最小 X。 */
    minX: number;
    /** AABB 最大 X。 */
    maxX: number;
    /** AABB 最小 Y。 */
    minY: number;
    /** AABB 最大 Y。 */
    maxY: number;
    /** 边长（x/y 平面欧氏距离）。 */
    length: number;
    /** 是否退化：边长不超过容差，视为采样异常点，不参与检测。 */
    degenerate: boolean;
}

/**
 * 判断两个点是否在容差内重合（仅 x/y 平面）。
 * @param pointA 点 A。
 * @param pointB 点 B。
 * @param epsilon 距离容差。
 * @returns 重合时返回 true。
 */
function arePointsCoincident(pointA: PolylinePointLike, pointB: PolylinePointLike, epsilon: number): boolean {
    const dx = pointA.x - pointB.x;
    const dy = pointA.y - pointB.y;
    return dx * dx + dy * dy <= epsilon * epsilon;
}

/**
 * 解析默认距离容差。
 * 说明：以输入点列包围盒对角线为尺度基准取 1e-9 相对容差，保证不同单位尺度下行为一致；
 * 下限 1e-12 兜底"全部点重合"等退化输入。
 * @param points 输入点列。
 * @returns 默认距离容差。
 */
function resolveDefaultEpsilon(points: readonly PolylinePointLike[]): number {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const point of points) {
        if (point.x < minX) minX = point.x;
        if (point.x > maxX) maxX = point.x;
        if (point.y < minY) minY = point.y;
        if (point.y > maxY) maxY = point.y;
    }
    const diagonal = Math.hypot(maxX - minX, maxY - minY);
    return Math.max(diagonal * 1e-9, 1e-12);
}

/**
 * 构建全部边数据。
 * @param points 输入点列。
 * @param edgeCount 边数（点数 - 1）。
 * @param epsilon 距离容差，用于退化边判定。
 * @returns 边数据列表。
 */
function buildEdgeData(
    points: readonly PolylinePointLike[],
    edgeCount: number,
    epsilon: number,
): PolylineEdgeData[] {
    const edges: PolylineEdgeData[] = [];
    for (let index = 0; index < edgeCount; index += 1) {
        const a = points[index]!;
        const b = points[index + 1]!;
        const length = Math.hypot(b.x - a.x, b.y - a.y);
        edges.push({
            a,
            b,
            minX: Math.min(a.x, b.x),
            maxX: Math.max(a.x, b.x),
            minY: Math.min(a.y, b.y),
            maxY: Math.max(a.y, b.y),
            length,
            degenerate: length <= epsilon,
        });
    }
    return edges;
}

/**
 * 判断边对是否属于"合法连接"而豁免自交判定。
 * 说明：
 * 1. 顺序相邻边（索引差为 1）共享折线顶点，是连续曲线的合法连接，直接豁免；
 * 2. 闭合折线（closed 为 true 且首尾点重合）中，首边（索引 0）与末边（索引 edgeCount - 1）
 *    共享接缝端点，同样豁免；首尾不重合时接缝边对按普通边对处理。
 * @param edgeIndexA 边 A 索引。
 * @param edgeIndexB 边 B 索引。
 * @param edgeCount 总边数。
 * @param closed 是否闭合折线。
 * @param points 输入点列（用于判定首尾是否重合）。
 * @param epsilon 距离容差。
 * @returns 应豁免时返回 true。
 */
function isExemptAdjacentPair(
    edgeIndexA: number,
    edgeIndexB: number,
    edgeCount: number,
    closed: boolean,
    points: readonly PolylinePointLike[],
    epsilon: number,
): boolean {
    const indexDiff = Math.abs(edgeIndexA - edgeIndexB);
    if (indexDiff === 1) {
        return true;
    }
    const lastEdgeIndex = edgeCount - 1;
    const isSeamPair = closed
        && ((edgeIndexA === 0 && edgeIndexB === lastEdgeIndex)
            || (edgeIndexA === lastEdgeIndex && edgeIndexB === 0));
    if (!isSeamPair) {
        return false;
    }
    // 闭合接缝豁免的前提：首尾点实际重合（共享端点），否则按普通边对参与检测。
    return arePointsCoincident(points[0]!, points[points.length - 1]!, epsilon);
}

/**
 * 两条线段（边）的精确相交判定（x/y 平面）。
 * 说明：
 * 1. 非平行：参数 t/u 闭区间判定（含端点接触），交点取边 A 上夹取后的参数点；
 * 2. 平行不共线：不相交；
 * 3. 共线：投影到主轴做区间重叠判定，重叠（含接触）时取重叠区间中点作为代表交点。
 * @param edgeA 边 A。
 * @param edgeB 边 B。
 * @param epsilon 距离容差（平行/共线判定与参数容差换算的基准）。
 * @returns 是否相交及代表交点；不相交时 point 为 null。
 */
function intersectSegments2D(
    edgeA: PolylineEdgeData,
    edgeB: PolylineEdgeData,
    epsilon: number,
): { intersects: boolean; point: PolylinePointLike | null } {
    const a1 = edgeA.a;
    const a2 = edgeA.b;
    const b1 = edgeB.a;
    const b2 = edgeB.b;
    const rX = a2.x - a1.x;
    const rY = a2.y - a1.y;
    const sX = b2.x - b1.x;
    const sY = b2.y - b1.y;
    const lengthR = Math.hypot(rX, rY);
    const lengthS = Math.hypot(sX, sY);
    const crossRS = rX * sY - rY * sX;
    const qpX = b1.x - a1.x;
    const qpY = b1.y - a1.y;
    // 平行判定：两方向向量夹角正弦（叉积 / 长度积）小于容差视为平行。
    const isParallel = Math.abs(crossRS) <= epsilon * lengthR * lengthS;

    if (!isParallel) {
        // 一般相交分支：t 为交点在边 A 上的参数，u 为交点在边 B 上的参数。
        const t = (qpX * sY - qpY * sX) / crossRS;
        const u = (qpX * rY - qpY * rX) / crossRS;
        // 距离容差换算为各自参数域容差，闭区间判定同时覆盖内部相交与端点接触。
        const tTolerance = epsilon / Math.max(lengthR, epsilon);
        const uTolerance = epsilon / Math.max(lengthS, epsilon);
        if (t >= -tTolerance && t <= 1 + tTolerance && u >= -uTolerance && u <= 1 + uTolerance) {
            const clampedT = Math.min(1, Math.max(0, t));
            return {
                intersects: true,
                point: {
                    x: a1.x + clampedT * rX,
                    y: a1.y + clampedT * rY,
                    z: a1.z + clampedT * (a2.z - a1.z),
                },
            };
        }
        return {intersects: false, point: null};
    }

    // 共线判定：边 B 起点到直线 AB 的距离（叉积 / 边长）小于容差视为共线。
    const crossQpR = qpX * rY - qpY * rX;
    if (Math.abs(crossQpR) > epsilon * lengthR) {
        return {intersects: false, point: null};
    }

    // 共线分支：选择变化量更大的轴做投影，避免退化轴导致区间判定失真。
    const useXAxis = Math.abs(rX) >= Math.abs(rY);
    const aProjection0 = useXAxis ? a1.x : a1.y;
    const aProjection1 = useXAxis ? a2.x : a2.y;
    const bProjection0 = useXAxis ? b1.x : b1.y;
    const bProjection1 = useXAxis ? b2.x : b2.y;
    const overlapLow = Math.max(Math.min(aProjection0, aProjection1), Math.min(bProjection0, bProjection1));
    const overlapHigh = Math.min(Math.max(aProjection0, aProjection1), Math.max(bProjection0, bProjection1));
    // 区间无重叠（含容差内的分离）即不相交。
    if (overlapHigh - overlapLow < -epsilon) {
        return {intersects: false, point: null};
    }
    // 重叠（含端点接触）：取重叠区间中点映射回边 A 上的参数点作为代表交点。
    const midProjection = (overlapLow + overlapHigh) / 2;
    const projectionSpan = aProjection1 - aProjection0;
    const projectedT = Math.abs(projectionSpan) > epsilon ? (midProjection - aProjection0) / projectionSpan : 0;
    const clampedT = Math.min(1, Math.max(0, projectedT));
    return {
        intersects: true,
        point: {
            x: a1.x + clampedT * rX,
            y: a1.y + clampedT * rY,
            z: a1.z + clampedT * (a2.z - a1.z),
        },
    };
}

/**
 * 使用空间哈希网格收集所有需要精确求交的候选边对。
 * 说明：
 * 1. 网格单元尺寸取全部非退化边的平均边长，使每条边平均覆盖约 1 个单元；
 *    单元尺寸只影响性能，不影响正确性（全覆盖插入保证 AABB 相交的边对必共享单元）；
 * 2. 同一边对可能因 AABB 横跨多个单元被多次收集，使用 visited 集合去重；
 * 3. 退化边直接跳过：零长度边是采样异常，不参与检测。
 * @param points 输入点列。
 * @param edges 边数据列表。
 * @param cellSize 网格单元尺寸。
 * @param closed 是否闭合折线。
 * @param epsilon 距离容差。
 * @returns 候选边对列表（保证 edgeIndexA < edgeIndexB 且已去重）。
 */
function collectCandidateEdgePairs(
    points: readonly PolylinePointLike[],
    edges: readonly PolylineEdgeData[],
    cellSize: number,
    closed: boolean,
    epsilon: number,
): Array<{edgeIndexA: number; edgeIndexB: number}> {
    const edgeCount = edges.length;
    // 空间哈希表：单元 key（"ix_iy"）→ 覆盖该单元的全部边索引。
    const grid = new Map<string, number[]>();
    const cellKey = (ix: number, iy: number): string => `${ix}_${iy}`;

    // 全覆盖插入：每条边落入其 AABB 覆盖的所有单元。
    for (let index = 0; index < edgeCount; index += 1) {
        const edge = edges[index]!;
        if (edge.degenerate) {
            continue;
        }
        const minIx = Math.floor(edge.minX / cellSize);
        const maxIx = Math.floor(edge.maxX / cellSize);
        const minIy = Math.floor(edge.minY / cellSize);
        const maxIy = Math.floor(edge.maxY / cellSize);
        for (let ix = minIx; ix <= maxIx; ix += 1) {
            for (let iy = minIy; iy <= maxIy; iy += 1) {
                const key = cellKey(ix, iy);
                let bucket = grid.get(key);
                if (!bucket) {
                    bucket = [];
                    grid.set(key, bucket);
                }
                bucket.push(index);
            }
        }
    }

    // 候选收集：只对"同格"边对精确求交；同格保证 AABB 相交，不漏报。
    const visitedPairs = new Set<string>();
    const candidates: Array<{edgeIndexA: number; edgeIndexB: number}> = [];
    for (let index = 0; index < edgeCount; index += 1) {
        const edge = edges[index]!;
        if (edge.degenerate) {
            continue;
        }
        const minIx = Math.floor(edge.minX / cellSize);
        const maxIx = Math.floor(edge.maxX / cellSize);
        const minIy = Math.floor(edge.minY / cellSize);
        const maxIy = Math.floor(edge.maxY / cellSize);
        for (let ix = minIx; ix <= maxIx; ix += 1) {
            for (let iy = minIy; iy <= maxIy; iy += 1) {
                const bucket = grid.get(cellKey(ix, iy));
                if (!bucket) {
                    continue;
                }
                for (const otherIndex of bucket) {
                    if (otherIndex <= index) {
                        continue;
                    }
                    if (isExemptAdjacentPair(index, otherIndex, edgeCount, closed, points, epsilon)) {
                        continue;
                    }
                    const pairKey = `${index}_${otherIndex}`;
                    if (visitedPairs.has(pairKey)) {
                        continue;
                    }
                    visitedPairs.add(pairKey);
                    candidates.push({edgeIndexA: index, edgeIndexB: otherIndex});
                }
            }
        }
    }
    return candidates;
}

/**
 * 检测折线是否自相交（含自触与共线重叠）。
 *
 * 使用方式示例：
 * ```ts
 * const result = detectPolylineSelfIntersection(curvePoints, {closed: true});
 * // result.isSelfIntersecting === true 时表示曲线自交
 * ```
 *
 * @param points 折线顶点列表（顺序连接，至少 3 个点）。
 * @param options 检测选项。
 * @returns 检测结果：是否自交、全部代表交点、相交边对列表。
 */
export function detectPolylineSelfIntersection(
    points: readonly PolylinePointLike[],
    options?: PolylineSelfIntersectionOptions,
): PolylineSelfIntersectionResult {
    const closed = options?.closed ?? false;
    const pointCount = points.length;
    // 少于 3 个点只构成 0~2 条边，不存在自交可能，直接返回空结果。
    if (pointCount < 3) {
        return {isSelfIntersecting: false, intersectionPoints: [], edgePairs: []};
    }
    const edgeCount = pointCount - 1;
    const epsilon = options?.epsilon ?? resolveDefaultEpsilon(points);
    const edges = buildEdgeData(points, edgeCount, epsilon);

    // 网格单元尺寸取平均边长：保证每条边平均覆盖约 1 个单元，兼顾插入与查询开销。
    let totalLength = 0;
    for (const edge of edges) {
        totalLength += edge.length;
    }
    const cellSize = Math.max(totalLength / edgeCount, epsilon * 4);

    const candidates = collectCandidateEdgePairs(points, edges, cellSize, closed, epsilon);

    // 对全部候选边对执行精确线段求交，聚合为最终结果。
    const edgePairs: PolylineSelfIntersectionEdgePair[] = [];
    for (const candidate of candidates) {
        const intersection = intersectSegments2D(
            edges[candidate.edgeIndexA]!,
            edges[candidate.edgeIndexB]!,
            epsilon,
        );
        if (intersection.intersects && intersection.point) {
            edgePairs.push({
                edgeIndexA: candidate.edgeIndexA,
                edgeIndexB: candidate.edgeIndexB,
                point: intersection.point,
            });
        }
    }
    return {
        isSelfIntersecting: edgePairs.length > 0,
        intersectionPoints: edgePairs.map((pair) => pair.point),
        edgePairs,
    };
}
